import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { mockTasks, mockProjects, mockUsers } from '../mock/data';
import { 
  Calendar, Clock, CheckCircle2, Circle, PlayCircle, 
  AlertCircle, ChevronRight, Filter
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import HeaderGlow from '../components/HeaderGlow';

const MyTasks = () => {
  const navigate = useNavigate();
  const { currentColorTheme } = useTheme();
  // In a real app, this would come from the auth context
  const currentUserId = '1'; // Sarah Johnson for demo
  const [statusFilter, setStatusFilter] = useState('all');

  // Filter tasks assigned to current user
  const myTasks = mockTasks.filter(task => task.assigneeId === currentUserId);
  
  // Filter by status
  const filteredTasks = statusFilter === 'all' 
    ? myTasks 
    : myTasks.filter(task => task.status === statusFilter);

  // Group tasks by status
  const tasksByStatus = {
    'todo': filteredTasks.filter(t => t.status === 'todo'),
    'in-progress': filteredTasks.filter(t => t.status === 'in-progress'),
    'completed': filteredTasks.filter(t => t.status === 'completed')
  };

  // Stats
  const stats = {
    total: myTasks.length,
    todo: myTasks.filter(t => t.status === 'todo').length,
    inProgress: myTasks.filter(t => t.status === 'in-progress').length,
    completed: myTasks.filter(t => t.status === 'completed').length,
    overdue: myTasks.filter(t => new Date(t.dueDate) < new Date() && t.status !== 'completed').length
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'in-progress': return <PlayCircle className="w-5 h-5 text-blue-500" />;
      default: return <Circle className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const isOverdue = (dueDate, status) => {
    return new Date(dueDate) < new Date() && status !== 'completed';
  };

  const TaskCard = ({ task }) => {
    const project = mockProjects.find(p => p.id === task.projectId);
    const overdue = isOverdue(task.dueDate, task.status);

    return (
      <Card 
        className={`bg-card border-border hover:shadow-md transition-all cursor-pointer group ${
          overdue ? 'border-l-4 border-l-red-500' : ''
        }`}
        onClick={() => navigate(`/projects/${task.projectId}`)}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <button className="mt-0.5" onClick={(e) => e.stopPropagation()}>
              {getStatusIcon(task.status)}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className={`font-medium text-foreground ${task.status === 'completed' ? 'line-through opacity-60' : ''}`}>
                    {task.title}
                  </h4>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {project?.name}
                  </p>
                </div>
                <Badge className={`${getPriorityColor(task.priority)} text-xs flex-shrink-0`}>
                  {task.priority}
                </Badge>
              </div>

              <div className="flex items-center flex-wrap gap-3 mt-3 text-sm">
                <div className={`flex items-center gap-1 ${overdue ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {overdue && <AlertCircle className="w-3 h-3" />}
                  <Calendar className="w-3 h-3" />
                  <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  <span>{task.loggedHours}/{task.estimatedHours}h</span>
                </div>
                {task.tags?.slice(0, 2).map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs text-foreground">{tag}</Badge>
                ))}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-8 bg-background min-h-screen">
      {/* Dynamic Header */}
      <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-pink-500 via-purple-500 to-indigo-500'} -mx-8 -mt-8 p-8`}>
        <h1 className="text-3xl font-bold text-white">My Tasks</h1>
        <p className="text-white/80">Your assigned tasks across all projects</p>
      </div>
      {/* Glowing separator line */}
      <div className="-mx-8">
        <HeaderGlow />
      </div>

      {/* Stats Cards - with top margin */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 mt-6">
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total Tasks</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-muted-foreground">{stats.todo}</p>
            <p className="text-sm text-muted-foreground">To Do</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-500">{stats.inProgress}</p>
            <p className="text-sm text-muted-foreground">In Progress</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-500">{stats.completed}</p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
        <Card className={`bg-card border-border ${stats.overdue > 0 ? 'border-red-500' : ''}`}>
          <CardContent className="p-4 text-center">
            <p className={`text-2xl font-bold ${stats.overdue > 0 ? 'text-red-500' : 'text-foreground'}`}>
              {stats.overdue}
            </p>
            <p className="text-sm text-muted-foreground">Overdue</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="all">All ({myTasks.length})</TabsTrigger>
          <TabsTrigger value="todo">To Do ({tasksByStatus['todo'].length})</TabsTrigger>
          <TabsTrigger value="in-progress">In Progress ({tasksByStatus['in-progress'].length})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({tasksByStatus['completed'].length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {/* Overdue Section */}
          {myTasks.filter(t => isOverdue(t.dueDate, t.status)).length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-red-500 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Overdue
              </h3>
              <div className="space-y-3">
                {myTasks.filter(t => isOverdue(t.dueDate, t.status)).map(task => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            </div>
          )}

          {/* All Tasks */}
          <div className="space-y-3">
            {filteredTasks.filter(t => !isOverdue(t.dueDate, t.status)).map(task => (
              <TaskCard key={task.id} task={task} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="todo" className="space-y-3">
          {tasksByStatus['todo'].map(task => (
            <TaskCard key={task.id} task={task} />
          ))}
          {tasksByStatus['todo'].length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Circle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No tasks to do</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="in-progress" className="space-y-3">
          {tasksByStatus['in-progress'].map(task => (
            <TaskCard key={task.id} task={task} />
          ))}
          {tasksByStatus['in-progress'].length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <PlayCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No tasks in progress</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-3">
          {tasksByStatus['completed'].map(task => (
            <TaskCard key={task.id} task={task} />
          ))}
          {tasksByStatus['completed'].length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No completed tasks</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyTasks;
